import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  CreditCard,
  Home,
  Settings,
  Sparkles,
  ArrowRight
} from 'lucide-react';

const MPConnectionSuccess: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll al top al montar
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-green-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        
        {/* Logo de Mercado Pago */}
        <div className="mb-6 flex justify-center">
          <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-teal-100">
            <img 
              src="https://http2.mlstatic.com/storage/logos-api-admin/a5f047d0-9be0-11ec-aad4-c3381f368aaf-xl@2x.png" 
              alt="Mercado Pago" 
              className="h-8 w-auto"
              onError={(e) => {
                // Fallback si la imagen no carga
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = '<span class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Mercado Pago</span>';
                }
              }}
            />
          </div>
        </div>

        {/* Ícono de éxito con animación */}
        <div className="relative mb-6">
          {/* Círculos animados de fondo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 bg-gradient-to-br from-teal-200 to-green-200 rounded-full animate-ping opacity-20"></div>
          </div>
          
          {/* Ícono principal */}
          <div className="relative w-20 h-20 bg-gradient-to-br from-teal-400 to-green-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Título */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3 text-center flex items-center justify-center gap-2">
          ¡Cuenta Vinculada!
          <Sparkles className="w-6 h-6 text-teal-500" />
        </h1>
        
        <p className="text-gray-600 mb-6 text-center">
          Tu cuenta de Mercado Pago ha sido vinculada exitosamente con ConsultAR.
        </p>

        {/* Features habilitados */}
        <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl p-5 mb-6 border border-teal-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-center flex items-center justify-center gap-2">
            <CreditCard className="w-5 h-5 text-teal-600" />
            Funcionalidades Activadas
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-6 h-6 bg-gradient-to-br from-teal-400 to-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">Cobros en línea</p>
                <p className="text-xs text-gray-600">Recibe pagos de forma segura</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-6 h-6 bg-gradient-to-br from-teal-400 to-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">Confirmación automática</p>
                <p className="text-xs text-gray-600">Los turnos se confirman al pagar</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-6 h-6 bg-gradient-to-br from-teal-400 to-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">Múltiples medios de pago</p>
                <p className="text-xs text-gray-600">Tarjetas, efectivo, transferencias</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-6 h-6 bg-gradient-to-br from-teal-400 to-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">Sin comisiones extras</p>
                <p className="text-xs text-gray-600">Solo las de Mercado Pago</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mensaje informativo */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Tus pacientes ahora podrán pagar directamente desde la plataforma al reservar un turno contigo.
            </p>
          </div>
        </div>

        {/* Botones */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/dashboard/profesional')}
            className="w-full bg-gradient-to-r from-teal-500 to-green-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 group"
          >
            <Home className="w-5 h-5" />
            Ir al Dashboard
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          
          <button
            onClick={() => navigate('/professional/payment-config')}
            className="w-full bg-white border-2 border-teal-200 text-teal-700 py-3 rounded-xl font-medium hover:bg-teal-50 transition-all flex items-center justify-center gap-2"
          >
            <Settings className="w-5 h-5" />
            Configurar Pagos
          </button>
        </div>

        {/* Footer note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
            <CreditCard className="w-3 h-3" />
            Puedes desvincular tu cuenta en cualquier momento
          </p>
        </div>
      </div>
    </div>
  );
};

export default MPConnectionSuccess;